import React, { useState } from 'react';

import type { ContactInteractionType, LeadFormData } from '../../../@types/contactInteraction';
import type { Owner } from '../../../@types/Owner';
import { useAdsModal } from '../../../hooks/useAdsModal';
import AdsModal from '../../common/AdsModal/AdsModal';
import MUITable from '../../common/MUITable';
import { formatDateFromIsoString, formatDpeDetailsFromOwner } from '../utils/formatters';
import CoproLocalOwned from './CoproLocalOwned';
import DpeDetailsPopup from './DpeDetailsPopup';
import OwnerInfos from './OwnerInfos';
import { LastContactInfo } from './PreviousContactList';

interface ExtendedInteraction {
  id: number;
  interactionType: ContactInteractionType;
  interactionAt: string;
  comment?: string;
  createdAt: string;
  meetingAt?: string;
}

interface LeadFormComponentProps {
  isOpen: boolean;
  onClose: () => void;
  owner: Owner;
  onSubmit: (data: LeadFormData) => void;
}

interface PendingTableProps {
  data: Owner[];
  interactionKey: 'salesManagerInteractions' | 'rentManagerInteractions';
  showMeetingColumn?: boolean;
  showContacts?: boolean;
  apiPagination?: boolean;
  LeadForm: React.ComponentType<LeadFormComponentProps>;
  totalCount?: number;
  onPageChange?: (page: number, rowsPerPage: number) => void;
  currentPage?: number;
  currentRowsPerPage?: number;
  onLeadSubmit?: (interactionId: number, formData: LeadFormData) => void;
  onRefresh?: () => void;
}

const PendingTable: React.FC<PendingTableProps> = ({
  data,
  interactionKey,
  showMeetingColumn = false,
  showContacts = false,
  apiPagination = false,
  LeadForm,
  totalCount,
  onPageChange,
  currentPage,
  currentRowsPerPage,
  onLeadSubmit,
  onRefresh,
}) => {
  const [selectedOwner, setSelectedOwner] = useState<Owner | null>(null);
  const [isLeadFormOpen, setIsLeadFormOpen] = useState(false);
  const [isDpeDetailsOpen, setIsDpeDetailsOpen] = useState(false);
  const [selectedDpe, setSelectedDpe] = useState<Record<string, unknown> | null>(null);
  const { handleAdsClick, adsModalProps } = useAdsModal();

  const handleLeadClick = (owner: Owner) => {
    setSelectedOwner(owner);
    setIsLeadFormOpen(true);
  };

  const handleLeadFormClose = () => {
    setIsLeadFormOpen(false);
    setSelectedOwner(null);
  };

  const handleLeadFormSubmit = (formData: LeadFormData) => {
    if (selectedOwner && selectedOwner.id && onLeadSubmit) {
      const interactions = (selectedOwner[interactionKey] ?? []) as ExtendedInteraction[];
      if (interactions.length > 0) {
        const sortedInteractions = [...interactions].sort((a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
        const interactionId = sortedInteractions[0].id;
        onLeadSubmit(interactionId, formData);
        handleLeadFormClose();
        if (onRefresh) {
          onRefresh();
        }
      }
    }
  };

  const handleDpeDetailsClick = (dpeDetails: Record<string, unknown>) => {
    setSelectedDpe(dpeDetails);
    setIsDpeDetailsOpen(true);
  };

  const getDpeDetails = (owner: Owner): React.ReactNode => {
    return formatDpeDetailsFromOwner(owner, handleDpeDetailsClick, handleAdsClick);
  };

  const getInteractionFromItem = (owner: Owner): ExtendedInteraction | null => {
    const interactions = owner[interactionKey];
    if (!interactions || interactions.length === 0) {
      return null;
    }
    const list = [...(interactions as ExtendedInteraction[])];
    return list.sort((a, b) =>
      new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
    )[0];
  };

  const getMeetingInfo = (interaction: ExtendedInteraction | null): React.ReactNode => {
    if (!interaction || !interaction.meetingAt) {
      return 'Pas de rendez-vous planifié';
    }
    // Same local date-time parsing as « Rendez-vous prévu » (LastContactInfo).
    return formatDateFromIsoString(interaction.meetingAt);
  };

  const getActionButtons = (owner: Owner): React.ReactNode => (
    <button
      className="rounded bg-rgpd-blue px-4 py-2 text-white"
      onClick={() => handleLeadClick(owner)}
    >
      Enregistrer le résultat du RDV
    </button>
  );

  const columnClass = showMeetingColumn ? 'w-1/6' : 'w-1/5';

  const tableData = data.map((owner) => {
    const ownerInteraction = getInteractionFromItem(owner);
    const baseRow: Record<string, React.ReactNode> = {
      'Propriétaire': <OwnerInfos owner={owner} showContacts={showContacts} />,
      'Propriété': <CoproLocalOwned owner={owner} />,
      'Dernier contact': <LastContactInfo owner={owner} />,
      'DPE': getDpeDetails(owner),
      'Actions': getActionButtons(owner),
    };
    if (showMeetingColumn) {
      baseRow['RDV prévu'] = getMeetingInfo(ownerInteraction);
    }
    const orderedKeys = showMeetingColumn
      ? ['Propriétaire', 'Propriété', 'Dernier contact', 'RDV prévu', 'DPE', 'Actions']
      : ['Propriétaire', 'Propriété', 'Dernier contact', 'DPE', 'Actions'];
    return Object.fromEntries(orderedKeys.map((k) => [k, baseRow[k]]));
  });

  const headers = showMeetingColumn
    ? [
        { title: 'Propriétaire', class: columnClass },
        { title: 'Propriété', class: columnClass },
        { title: 'Dernier contact', class: columnClass },
        { title: 'RDV prévu', class: columnClass },
        { title: 'DPE', class: columnClass },
        { title: 'Actions', class: columnClass },
      ]
    : [
        { title: 'Propriétaire', class: columnClass },
        { title: 'Propriété', class: columnClass },
        { title: 'Dernier contact', class: columnClass },
        { title: 'DPE', class: columnClass },
        { title: 'Actions', class: columnClass },
      ];

  return (
    <>
      <MUITable
        data={tableData}
        headers={headers}
        isSearchable={true}
        tableClass="w-full"
        disablePagination={false}
        totalCount={totalCount}
        onPageChange={onPageChange}
        currentPage={currentPage}
        currentRowsPerPage={currentRowsPerPage}
        apiPagination={apiPagination}
      />

      {selectedOwner && (
        <LeadForm
          isOpen={isLeadFormOpen}
          onClose={handleLeadFormClose}
          onSubmit={handleLeadFormSubmit}
          owner={selectedOwner}
        />
      )}

      {selectedDpe && (
        <DpeDetailsPopup
          isOpen={isDpeDetailsOpen}
          onClose={() => setIsDpeDetailsOpen(false)}
          dpeDetails={selectedDpe}
        />
      )}

      <AdsModal {...adsModalProps} />
    </>
  );
};

export default PendingTable;
